<div class="modal-content">
  <div class="modal-header align-items-center">
    <div class="d-flex flex-column">
      <h2 class="modal-title mb-1">
        @if (isRenewal) {
          {{ 'purchaseMembership.renewTitle' | translate }}
        } @else if (company) {
          {{ 'purchaseMembership.joinTitle' | translate }} {{ company.company_name }}
        } @else {
          {{ 'purchaseMembership.title' | translate }}
        }
      </h2>
      <p class="text-muted mb-0">
        @if (isRenewal) {
          {{ 'purchaseMembership.renewSubtitle' | translate }} {{ renewPlan?.name }} {{ 'purchaseMembership.renewSubtitleSuffix' | translate }}
        } @else {
          {{ 'purchaseMembership.subtitle' | translate }}
        }
      </p>
    </div>
    <i class="fa-solid fa-xmark fs-2 cursor-pointer" (click)="close()"></i>
  </div>

  <div class="modal-body">
    @if (loading) {
      <div class="text-center py-5">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">{{ 'purchaseMembership.loading' | translate }}</span>
        </div>
      </div>
    } @else {
      <!-- Renewal Mode: Show single plan -->
      @if (isRenewal) {
        <div class="row g-4">
          @for (plan of availablePlans; track plan.plan_id) {
            <div class="col-12">
              <div
                class="card cursor-pointer membership-plan-card selected border-primary"
                (click)="selectPlan(plan)">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <div class="d-flex align-items-center mb-2">
                        <h4 class="mb-0 me-3">{{ plan.name }}</h4>
                        <span class="badge" [class.badge-primary]="plan.coverage === 'individual'" [class.badge-success]="plan.coverage === 'family'">
                          {{ plan.coverage | titlecase }}
                        </span>
                      </div>
                      @if (plan.description) {
                        <p class="text-muted mb-3">{{ plan.description }}</p>
                      }
                    </div>
                    <div class="text-end">
                      <h3 class="mb-0 text-primary">{{ formatPrice(plan.price_cents) }} kr</h3>
                      <small class="text-muted">{{ 'purchaseMembership.per' | translate }} {{ plan.billing_cycle }}</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      } @else {
        <!-- Browse Mode: Grouped by organisation -->
        @if (availablePlans.length === 0) {
          <div class="alert alert-info">
            @if (plans.length === 0) {
              {{ 'purchaseMembership.noPlans' | translate }}
            } @else {
              {{ 'purchaseMembership.alreadyMember' | translate }}
            }
          </div>
        } @else {
          <!-- Search Field -->
          @if (!companyId && companyGroups.length > 1) {
            <div class="search-container mb-4">
              <div class="position-relative">
                <i class="fa fa-search search-icon"></i>
                <input
                  type="text"
                  class="form-control form-control-lg search-input"
                  [(ngModel)]="searchQuery"
                  (ngModelChange)="onSearchChange()"
                  [placeholder]="'purchaseMembership.searchPlaceholder' | translate"
                />
                @if (searchQuery) {
                  <i class="fa fa-times clear-icon cursor-pointer" (click)="searchQuery = ''; onSearchChange()"></i>
                }
              </div>
              <div class="mt-2 text-muted small">
                {{ filteredGroups.length }} {{ 'purchaseMembership.organisationsFound' | translate }}
              </div>
            </div>
          }

          <!-- Organisation Groups -->
          <div class="organisation-groups">
            @for (group of filteredGroups; track group.companyId) {
              <div class="organisation-card mb-3" [class.expanded]="group.isExpanded">
                <!-- Organisation Header (Clickable) -->
                <div class="organisation-header" (click)="toggleGroup(group)">
                  <div class="d-flex align-items-center flex-grow-1">
                    <!-- Logo -->
                    <div class="organisation-logo me-3">
                      @if (group.companyLogo) {
                        <img [src]="group.companyLogo" [alt]="group.companyName" />
                      } @else {
                        <div class="logo-placeholder">
                          <i class="fa fa-building"></i>
                        </div>
                      }
                    </div>

                    <!-- Organisation Info -->
                    <div class="organisation-info flex-grow-1">
                      <h5 class="organisation-name mb-1">{{ group.companyName }}</h5>
                      <div class="organisation-meta d-flex flex-wrap gap-2">
                        <span class="badge badge-light-primary">
                          <i class="fa fa-tag me-1"></i>{{ group.planCount }} {{ group.planCount === 1 ? ('purchaseMembership.plan' | translate) : ('purchaseMembership.plans' | translate) }}
                        </span>
                        <span class="badge badge-light-success">
                          <i class="fa fa-coins me-1"></i>{{ getPriceRange(group) }}
                        </span>
                        @for (type of getPlanTypes(group); track type) {
                          <span class="badge" [class.badge-light-info]="type === 'individual'" [class.badge-light-warning]="type === 'family'">
                            <i class="fa me-1" [class.fa-user]="type === 'individual'" [class.fa-users]="type === 'family'"></i>
                            {{ type | titlecase }}
                          </span>
                        }
                      </div>
                    </div>
                  </div>

                  <!-- Expand Icon -->
                  <div class="expand-icon">
                    <i class="fa" [class.fa-chevron-down]="!group.isExpanded" [class.fa-chevron-up]="group.isExpanded"></i>
                  </div>
                </div>

                <!-- Plans (Expandable) -->
                @if (group.isExpanded) {
                  <div class="organisation-plans">
                    @for (plan of group.plans; track plan.plan_id) {
                      <div
                        class="plan-card"
                        [class.selected]="selectedPlan?.plan_id === plan.plan_id"
                        (click)="selectPlan(plan); $event.stopPropagation()">
                        <div class="d-flex justify-content-between align-items-start">
                          <div class="flex-grow-1">
                            <div class="d-flex align-items-center mb-2">
                              <h6 class="mb-0 me-2">{{ plan.name }}</h6>
                              <span class="badge badge-sm" [class.badge-primary]="plan.coverage === 'individual'" [class.badge-success]="plan.coverage === 'family'">
                                {{ plan.coverage | titlecase }}
                              </span>
                            </div>
                            @if (plan.description) {
                              <p class="text-muted small mb-2">{{ plan.description }}</p>
                            }
                            <div class="d-flex flex-wrap gap-1">
                              <span class="badge badge-light-secondary badge-sm">
                                <i class="fa fa-tag me-1"></i>{{ plan.code }}
                              </span>
                              <span class="badge badge-light-secondary badge-sm">
                                <i class="fa fa-calendar me-1"></i>{{ plan.billing_cycle | titlecase }}
                              </span>
                              @if (plan.max_children) {
                                <span class="badge badge-light-secondary badge-sm">
                                  <i class="fa fa-child me-1"></i>{{ 'purchaseMembership.maxChildren' | translate }} {{ plan.max_children }}
                                </span>
                              }
                            </div>
                          </div>
                          <div class="text-end">
                            <div class="plan-price">{{ formatPrice(plan.price_cents) }} kr</div>
                            <small class="text-muted">/ {{ plan.billing_cycle }}</small>
                          </div>
                        </div>

                        <!-- Benefits -->
                        @if (plan.benefits && plan.benefits.length > 0) {
                          <div class="plan-benefits mt-3">
                            @for (benefit of plan.benefits.slice(0, 3); track $index) {
                              <div class="benefit-item">
                                <i class="fa fa-check text-success me-2"></i>
                                <span class="small">{{ benefit }}</span>
                              </div>
                            }
                            @if (plan.benefits.length > 3) {
                              <div class="benefit-item text-muted">
                                <i class="fa fa-plus me-2"></i>
                                <span class="small">{{ plan.benefits.length - 3 }} {{ 'purchaseMembership.morebenefits' | translate }}</span>
                              </div>
                            }
                          </div>
                        }

                        <!-- Selected Indicator -->
                        @if (selectedPlan?.plan_id === plan.plan_id) {
                          <div class="selected-indicator mt-3">
                            <i class="fa fa-check-circle text-primary me-2"></i>
                            <span class="text-primary fw-bold">{{ 'purchaseMembership.selected' | translate }}</span>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            }

            @if (filteredGroups.length === 0 && searchQuery) {
              <div class="text-center py-5 text-muted">
                <i class="fa fa-search fs-3x mb-3 d-block"></i>
                {{ 'purchaseMembership.noSearchResults' | translate }}
              </div>
            }
          </div>
        }
      }
    }
  </div>

  <div class="modal-footer">
    <div class="row w-100">
      <div class="col-12 d-flex justify-content-between align-items-center">
        @if (selectedPlan) {
          <div class="selected-plan-summary">
            <span class="text-muted">{{ 'purchaseMembership.selectedPlan' | translate }}:</span>
            <span class="fw-bold ms-2">{{ selectedPlan.name }}</span>
            <span class="text-primary ms-2">{{ formatPrice(selectedPlan.price_cents) }} kr</span>
          </div>
        } @else {
          <div></div>
        }
        <div class="d-flex gap-2">
          <app-button
            [variant]="'neutral'"
            [label]="'purchaseMembership.cancelBtn' | translate"
            (clicked)="close()"
          />
          <app-button
            [variant]="'primary'"
            [label]="isPaymentRequired() ? (isRenewal ? ('purchaseMembership.renewWithStripe' | translate) : ('purchaseMembership.payWithStripe' | translate)) : (isRenewal ? ('purchaseMembership.renew' | translate) : ('purchaseMembership.purchase' | translate))"
            [iconClass]="isPaymentRequired() ? 'fab fa-stripe' : 'fa fa-check'"
            [disabled]="!selectedPlan"
            [loading]="purchasing"
            (clicked)="purchaseMembership()"
          />
        </div>
      </div>
    </div>
  </div>
</div>
